// VisionLauncher — el "menú" fullscreen (⌘K) estilo Launchpad. Portal a body,
// sobre el wallpaper. Header con marca + cerrar, buscador, grilla paginada de 9
// navegable por teclado (↑↓←→ + Enter) y barra de atajos abajo.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CornerDownLeft, LayoutGrid, Search, X } from "lucide-react";

import { CATALOG, type LauncherItem } from "@/components/vision/catalog";
import { openTriageModule } from "@/lib/triage-bridge";

const COLS = 3;
const PAGE_SIZE = 9;

export function VisionLauncher({
  open,
  onClose,
  isTeacher,
  isAdmin,
}: {
  open: boolean;
  onClose: () => void;
  isTeacher: boolean;
  isAdmin: boolean;
}) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const items = useMemo(() => {
    const base = CATALOG.filter(
      (i) => (i.teacherOnly ? isTeacher : true) && (i.adminOnly ? isAdmin : true),
    );
    const needle = q.trim().toLowerCase();
    if (!needle) return base;
    return base.filter(
      (i) =>
        i.label.toLowerCase().includes(needle) ||
        (i.description ?? "").toLowerCase().includes(needle),
    );
  }, [q, isTeacher, isAdmin]);

  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const page = Math.min(Math.floor(sel / PAGE_SIZE), pages - 1);
  const pageItems = items.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const activate = useCallback(
    (item: LauncherItem | undefined) => {
      if (!item) return;
      onClose();
      if (item.action === "triage") {
        openTriageModule();
        return;
      }
      if (item.path) navigate(item.path);
    },
    [navigate, onClose],
  );

  useEffect(() => {
    if (open) {
      setQ("");
      setSel(0);
    }
  }, [open]);

  // Al filtrar, mantener la selección dentro de rango.
  useEffect(() => {
    setSel((s) => Math.min(s, Math.max(0, items.length - 1)));
  }, [items.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") return onClose();
      if (e.key === "Enter") {
        e.preventDefault();
        return activate(items[sel]);
      }
      const n = items.length;
      if (!n) return;
      let next = sel;
      if (e.key === "ArrowRight") next = Math.min(n - 1, sel + 1);
      else if (e.key === "ArrowLeft") next = Math.max(0, sel - 1);
      else if (e.key === "ArrowDown") next = Math.min(n - 1, sel + COLS);
      else if (e.key === "ArrowUp") next = Math.max(0, sel - COLS);
      else return;
      e.preventDefault();
      setSel(next);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, items, sel, activate]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex flex-col"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <button aria-label="Cerrar menú" className="vision-backdrop absolute inset-0" onClick={onClose} />

          {/* Header: marca + cerrar */}
          <div className="relative z-10 flex items-center justify-between px-6 py-4 sm:px-9">
            <div className="flex items-center gap-3">
              <span
                className="flex h-9 w-9 items-center justify-center rounded-[11px] text-white shadow-lg"
                style={{ background: "linear-gradient(150deg, var(--role-accent), var(--role-accent-to))" }}
              >
                <LayoutGrid size={18} />
              </span>
              <div className="leading-tight">
                <div className="text-[15px] font-bold text-[var(--color-text-primary)]">drnewenia</div>
                <div className="font-[var(--font-mono)] text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
                  Aplicaciones
                </div>
              </div>
            </div>
            <button
              aria-label="Cerrar"
              onClick={onClose}
              className="vision-focusable flex h-10 w-10 items-center justify-center rounded-full border border-[var(--vision-glass-border)] bg-[var(--color-surface)]/60 text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
            >
              <X size={18} />
            </button>
          </div>

          {/* Cluster centrado verticalmente (buscador + grilla + paginación).
              m-auto centra si cabe y permite scroll desde arriba si no. */}
          <div className="relative z-10 flex flex-1 flex-col overflow-y-auto px-4">
            <div className="m-auto flex w-full flex-col items-center py-2">
              {/* Buscador */}
              <div className="w-full max-w-[620px]">
                <div className="vision-search flex items-center gap-3 px-5 py-3">
                  <Search size={17} className="shrink-0 text-[var(--color-text-muted)]" />
                  <input
                    ref={inputRef}
                    autoFocus
                    value={q}
                    onChange={(e) => {
                      setQ(e.target.value);
                      setSel(0);
                    }}
                    placeholder="Buscar módulo…"
                    className="w-full bg-transparent text-[15px] text-[var(--color-text-primary)] outline-none placeholder:text-[var(--color-text-muted)]"
                  />
                  <span className="hidden items-center gap-1.5 rounded-full border border-[var(--vision-glass-border)] px-2.5 py-1 text-[11px] text-[var(--color-text-muted)] sm:flex">
                    <CornerDownLeft size={12} /> abrir
                  </span>
                </div>
              </div>

              {/* Grilla — siempre 3×3 (rellena con celdas invisibles para altura constante) */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={page + "|" + q}
                  className="mt-6 grid w-full max-w-[660px] grid-cols-3 gap-4"
                  initial="hidden"
                  animate="visible"
                  exit={{ opacity: 0 }}
                  variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.028 } } }}
                >
                  {items.length === 0 ? (
                    <div className="col-span-3 py-10 text-center text-[13px] text-[var(--color-text-muted)]">
                      Sin módulos para “{q}”.
                    </div>
                  ) : (
                    Array.from({ length: PAGE_SIZE }).map((_, li) => {
                      const item = pageItems[li];
                      if (!item) return <div key={"ph" + li} aria-hidden className="invisible aspect-[4/3.1]" />;
                      const gi = page * PAGE_SIZE + li;
                      return (
                        <motion.button
                          key={item.label}
                          variants={{ hidden: { opacity: 0, scale: 0.92, y: 10 }, visible: { opacity: 1, scale: 1, y: 0 } }}
                          transition={{ type: "spring", stiffness: 380, damping: 28 }}
                          onClick={() => activate(item)}
                          onMouseEnter={() => setSel(gi)}
                          className={`vision-tile vision-focusable flex aspect-[4/3.1] flex-col items-center justify-center gap-2.5 p-4 text-center ${gi === sel ? "vision-tile-active" : ""}`}
                        >
                          <span
                            className="vision-app-icon flex h-14 w-14 items-center justify-center text-white"
                            style={{
                              background: `linear-gradient(150deg, ${item.gradient[0]}, ${item.gradient[1]})`,
                              boxShadow: `0 12px 26px -8px ${item.color}88`,
                            }}
                          >
                            <item.icon size={26} />
                          </span>
                          <span className="text-[13.5px] font-semibold leading-tight text-[var(--color-text-primary)]">
                            {item.label}
                          </span>
                          {item.description && (
                            <span className="text-[11px] leading-tight text-[var(--color-text-muted)]">
                              {item.description}
                            </span>
                          )}
                        </motion.button>
                      );
                    })
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Paginación en cápsula */}
              {pages > 1 && (
                <div className="flex justify-center pt-6">
                  <div className="flex items-center gap-1.5 rounded-full border border-[var(--vision-glass-border)] bg-[var(--color-surface)]/70 px-2.5 py-1.5 shadow-sm">
                    {Array.from({ length: pages }).map((_, p) => (
                      <button
                        key={p}
                        aria-label={`Página ${p + 1}`}
                        onClick={() => setSel(p * PAGE_SIZE)}
                        className={`h-1.5 rounded-full transition-all ${p === page ? "w-5 bg-[var(--role-accent)]" : "w-1.5 bg-[var(--color-text-muted)]/45 hover:bg-[var(--color-text-muted)]/70"}`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer: ayuda + atajos de teclado */}
          <div className="relative z-10 flex flex-col items-center gap-2 pb-5 pt-3">
            <p className="text-[12px] text-[var(--color-text-muted)]">
              Escribe para encontrar un módulo, o navega con el teclado.
            </p>
            <div className="flex items-center gap-3 rounded-full border border-[var(--vision-glass-border)] bg-[var(--color-surface)]/50 px-4 py-1.5 text-[11px] text-[var(--color-text-muted)] backdrop-blur">
              <span><kbd className="font-[var(--font-mono)]">↑↓←→</kbd> navegar</span>
              <span className="opacity-40">·</span>
              <span><kbd className="font-[var(--font-mono)]">↵</kbd> abrir</span>
              <span className="opacity-40">·</span>
              <span><kbd className="font-[var(--font-mono)]">Esc</kbd> cerrar</span>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
